import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, MaskLine } from "./Reveal";

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-28 lg:py-40 px-6 lg:px-10 scroll-mt-20 bg-bgblue"
      data-testid="about-section"
    >
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
        {/* Arched image over terrazzo */}
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div
              className="absolute -inset-5 rounded-[3rem] opacity-30"
              style={{
                backgroundImage:
                  "url(https://images.pexels.com/photos/4977451/pexels-photo-4977451.jpeg)",
                backgroundSize: "cover",
              }}
              aria-hidden="true"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative aspect-[4/5] rounded-t-full rounded-b-[2.5rem] overflow-hidden border border-cocoa/10"
              data-testid="about-image"
            >
              <motion.img
                src="/flavors/shop.png"
                alt="The cosy Odd Scoops neighbourhood shopfront with outdoor tables"
                style={{ y }}
                className="absolute inset-0 w-full h-[116%] object-cover -top-[8%]"
              />
            </motion.div>
          </div>
        </div>

        {/* Text */}
        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal>
            <p className="uppercase tracking-[0.3em] text-xs font-500 text-cocoa/50 mb-6">
              The Odd Scoops story
            </p>
          </Reveal>
          <h2 className="font-heading font-600 text-cocoa leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-[3.75rem]">
            <MaskLine>We fell for the</MaskLine>
            <MaskLine delay={0.08} className="text-berry">odd &amp; the honest.</MaskLine>
          </h2>
          <Reveal delay={0.2}>
            <div className="mt-8 space-y-5 max-w-xl text-cocoa/75 text-base sm:text-lg leading-relaxed">
              <p>
                odd scoops began in a small Bengaluru kitchen with a stubborn belief:
                gelato should taste of the thing it&apos;s named after. No shortcuts,
                no mystery powders, no neon colours.
              </p>
              <p>
                We source the best we can find — Alphonso mangoes in summer, Belgian
                chocolate, farm milk, real pistachios — and churn slowly, in small batches,
                so every scoop stays dense, silky and unmistakably fresh.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-x-12 gap-y-6">
              {[
                ["30+", "Rotating flavours"],
                ["100%", "Natural ingredients"],
                ["1", "Tiny Bengaluru kitchen"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-heading font-600 text-4xl text-cocoa">{n}</div>
                  <div className="text-sm text-cocoa/60 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
